import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, pollJob, jobErrorMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { CalendarRange, Loader2, CheckCircle2, Circle, XCircle } from "lucide-react";
import { toast } from "sonner";

export const PrepareWeekCard = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [offers, setOffers] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/marketing/campaigns").then((r) => {
      setCampaigns(r.data);
      const active = r.data.find((c) => c.status === "Active") || r.data[0];
      if (active) setCampaignId(active.id);
    }).catch(() => {});
    api.get("/marketing/offers").then((r) => setOffers(r.data.filter((o) => o.status === "Active"))).catch(() => {});
  }, []);

  const camp = campaigns.find((c) => c.id === campaignId);
  const hasActiveOffer = offers.some((o) => o.id === camp?.offer_id);

  const prepare = async () => {
    if (!campaignId) return toast.error("Select a campaign first");
    setBusy(true); setSteps([]); setResult(null);
    try {
      const { data } = await api.post("/marketing/prepare-week", { campaign_id: campaignId, goal_text: goal || undefined });
      if (data.duplicate) {
        toast.info(data.message || "A current batch already exists.");
        setBusy(false);
        return;
      }
      const job = await pollJob(data.job_id, { interval: 2500, timeout: 600000, onUpdate: (j) => setSteps(j.steps || []) });
      if (job.status === "Failed" || job.status === "Cancelled") {
        toast.error(`Prepare Next Week failed — ${jobErrorMessage(job)}`);
      } else {
        setResult(job.result);
        toast.success("Weekly package ready in the Approval Inbox");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Prepare Next Week");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-gradient-to-br from-[#581C87] to-[#2D1B3D] rounded-xl p-6 md:p-8 mb-6" data-testid="prepare-week-card">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-purple-200 font-semibold flex items-center gap-1.5">
            <CalendarRange className="h-3.5 w-3.5" /> One-Click Weekly Workflow
          </p>
          <h2 className="font-heading text-2xl font-bold text-white mt-1">Prepare Next Week</h2>
          <p className="text-purple-100 text-sm mt-2 max-w-xl">
            AI plans strategy, writes a 7-day content week, runs compliance, produces branded media and builds a proposed calendar — all into one Approval Inbox. Nothing publishes until you approve.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 max-w-2xl">
            <div>
              <Label className="text-purple-200 text-xs">Campaign</Label>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger className="bg-white/95 border-0 mt-1" data-testid="prepare-week-campaign"><SelectValue placeholder="Select campaign" /></SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.status}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-purple-200 text-xs">Enrollment goal (optional)</Label>
              <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="e.g. Fill the next Clinical Trichology cohort" className="bg-white/95 border-0 mt-1" data-testid="prepare-week-goal" />
            </div>
          </div>
          {camp && !hasActiveOffer && (
            <p className="text-amber-200 text-xs mt-2" data-testid="prepare-week-offer-warning">This campaign needs an active offer before you can prepare a week.</p>
          )}
        </div>
        <Button onClick={prepare} disabled={busy || !campaignId} className="bg-white text-[#581C87] hover:bg-purple-50 h-11 px-6 shrink-0" data-testid="prepare-week-button">
          {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing…</> : <>Prepare Next Week</>}
        </Button>
      </div>

      {steps.length > 0 && (
        <div className="mt-6 bg-white/10 rounded-lg p-4" data-testid="prepare-week-steps">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2 py-1 text-sm text-white" data-testid={`prepare-step-${i}`}>
              {s.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                : s.status === "running" ? <Loader2 className="h-4 w-4 animate-spin text-purple-200" />
                : s.status === "error" ? <XCircle className="h-4 w-4 text-red-300" />
                : <Circle className="h-4 w-4 text-purple-300/50" />}
              <span className={s.status === "pending" ? "text-purple-200/70" : ""}>{s.key}</span>
              {s.note && <span className="text-purple-200/80 text-xs">— {s.note}</span>}
            </div>
          ))}
        </div>
      )}

      {result && (
        <div className="mt-4 bg-white rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid="prepare-week-result">
          <div className="text-sm text-gray-700">
            <strong>{result.concepts}</strong> concepts · <strong>{result.variants}</strong> platform variants · <strong>{result.passing}</strong> pass compliance · <strong>{result.needs_review}</strong> need review · <strong>{result.media_items}</strong> media item(s)
          </div>
          <Button onClick={() => navigate("/marketing/approval-inbox")} className="bg-[#581C87] text-white hover:bg-[#6B21A8]" data-testid="prepare-week-goto-inbox">Review in Approval Inbox →</Button>
        </div>
      )}
    </div>
  );
};
